/**
 * Created: 2026-03-18
 * Initial commit: Add order detail dialog for admin order view
 *
 * Dialog component that shows detailed order information when an order row
 * is clicked in the OrdersPage. Adapted from tc-mini's order page for the
 * admin frontend, without customer-facing mutations.
 */

import { gql } from '@apollo/client'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react'
import { currency, dateFormat, message } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  type MergedSlot,
  mergeConsecutiveSlots,
} from '@tc-app/shared/booking/mergeConsecutiveSlots'
import { useState } from 'react'
import {
  CouponStatus,
  GameFormat,
  GetOrderDetailDocument,
  OrderCouponDocument,
  OrderStatus,
  RefundOrderDocument,
  SiteRole,
} from '../gql/graphql'
import { useRole } from '../hooks'
import { formatEventTimeRange, getWeekdayText } from '../lib/dateUtils'
import { COUPON_CATEGORY_LABEL, SPORT_LABEL } from '../lib/domainLabels'
import NumberInput, { type NumberInputChangeEvent } from './NumberInput'
import UserAvatar from './UserAvatar'

// Created: 2026-03-18 | Initial commit: Add order detail query and refund mutation for admin dialog
gql`
  query GetOrderDetail($id: UUID!) {
    order(id: $id) {
      id
      siteId
      status
      amount
      detail {
        __typename
        ... on SlotBookingList {
          items {
            courtId
            courtName
            price
            startTime
            cancelledAt
          }
        }
        ... on EventSignupDetail {
          eventId
          eventName
          gameFormat
          startTime
          duration
          price
        }
        ... on CourseDetail {
          courseId
          courseName
          hours
          price
        }
      }
      user {
        id
        name
        mobile
        avatar
        gender
        openid
      }
      payments {
        id
        amount
        createTime
        payTime
        creatorName
        couponId
      }
      createTime
      payTime
      cancelTime
      expiresAt
    }
  }

  query OrderCoupon($couponId: UUID!) {
    coupon(couponId: $couponId) {
      id
      issuedAt
      expiresAt
      status
      discountedAmount
      redeemedAt
      issuer {
        id
        openid
        name
        avatar
        gender
      }
      template {
        id
        name
        category
        sport
        discountAmount
      }
    }
  }

  mutation RefundOrder($orderId: UUID!, $amount: Decimal!) {
    refundOrder(orderId: $orderId, amount: $amount) {
      ... on GraphqlSuccess {
        __typename
      }
      ... on GraphqlRequestError {
        __typename
        code
        message
      }
    }
  }
`

const statusLabels: Record<
  OrderStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'error' }
> = {
  [OrderStatus.Pending]: { label: '待支付', color: 'warning' },
  [OrderStatus.Confirmed]: { label: '已支付', color: 'success' },
  [OrderStatus.Cancelled]: { label: '已取消', color: 'default' },
  [OrderStatus.Closed]: { label: '已关闭', color: 'error' },
}

const couponStatusLabels: Record<CouponStatus, string> = {
  [CouponStatus.Active]: '有效',
  [CouponStatus.Used]: '已使用',
  [CouponStatus.Voided]: '已作废',
  [CouponStatus.Expired]: '已过期',
}

interface OrderDialogProps {
  orderId: string | null
  onClose: () => void
}

export default function OrderDialog({ orderId, onClose }: OrderDialogProps) {
  const { currentSite } = useRole()
  const isSuperAdmin = currentSite?.roles.includes(SiteRole.SuperAdmin) ?? false

  const { data, loading, refetch } = useQuery(GetOrderDetailDocument, {
    variables: { id: orderId ?? '' },
    skip: !orderId,
    fetchPolicy: 'network-only',
  })

  const [refundMutation, { loading: refunding }] = useMutation(RefundOrderDocument)

  const [refundOpen, setRefundOpen] = useState(false)
  const [refundAmount, setRefundAmount] = useState<number | null>(null)
  const [refundError, setRefundError] = useState('')
  const [couponOpen, setCouponOpen] = useState(false)

  const [loadCoupon, { data: couponData, loading: couponLoading }] = useLazyQuery(
    OrderCouponDocument,
    { fetchPolicy: 'network-only' },
  )

  const order = data?.order
  const coupon = couponData?.coupon

  const mergedSlots = mergeConsecutiveSlots(
    order?.detail?.__typename === 'SlotBookingList'
      ? order.detail.items.map((slot) => ({
          courtId: slot.courtId,
          courtName: slot.courtName,
          startTime: slot.startTime,
          price: Number(slot.price),
          cancelledAt: slot.cancelledAt,
        }))
      : [],
  )

  const netPaid = order
    ? order.payments
        .filter((p) => p.couponId == null)
        .reduce((sum, p) => sum + Number(p.amount), 0)
    : 0
  const maxRefund = Math.round(netPaid * 100) / 100

  const refundValid =
    refundAmount !== null && refundAmount >= 0.01 && refundAmount <= maxRefund

  return (
    <Dialog open={!!orderId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        订单详情
        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {order && (
            <Chip
              label={statusLabels[order.status].label}
              color={statusLabels[order.status].color}
              size="small"
            />
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex' }}>
          <IconButton onClick={() => refetch()} size="small" disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.100', p: 1.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {order && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Slot details */}
            {mergedSlots.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    预订详情
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>场地 / 时段</TableCell>
                        <TableCell align="right">单价</TableCell>
                        <TableCell align="right">小时</TableCell>
                        <TableCell align="right">小计</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mergedSlots.map((slot: MergedSlot) => {
                        const weekday = getWeekdayText(slot.date)
                        const startHour = slot.startHour.toString().padStart(2, '0')
                        const endHour = slot.endHour.toString().padStart(2, '0')
                        const hours = slot.endHour - slot.startHour
                        const unitPrice = slot.price / hours

                        return (
                          <TableRow
                            key={`${slot.courtId}-${slot.date}-${slot.startHour}`}
                          >
                            <TableCell>
                              <div>{slot.courtName}</div>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                {slot.date} {weekday} {startHour}:00-{endHour}:00
                              </Typography>
                              {slot.cancelledAt && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label="已取消"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      mr: 0.5,
                                      borderColor: 'error.main',
                                      color: 'error.main',
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'error.main' }}
                                  >
                                    {dateFormat(slot.cancelledAt, {
                                      format: 'yyyy-MM-dd HH:mm:ss',
                                    })}
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="right">¥{unitPrice}</TableCell>
                            <TableCell align="right">x{hours}</TableCell>
                            <TableCell align="right">¥{slot.price}</TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0,
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'text.primary',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {order.status === OrderStatus.Pending
                        ? '待支付金额:'
                        : '订单总额:'}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: 'error.main',
                        fontWeight: 'bold',
                      }}
                    >
                      ¥{Number(order.amount).toFixed(2)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Event signup details */}
            {order.detail?.__typename === 'EventSignupDetail' && (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    报名详情
                  </Typography>
                  <Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        活动
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={
                            order.detail.gameFormat === GameFormat.Freeplay
                              ? '畅打'
                              : '比赛'
                          }
                          color={
                            order.detail.gameFormat === GameFormat.Freeplay
                              ? 'secondary'
                              : 'primary'
                          }
                          size="small"
                        />
                        <Typography variant="body2">
                          {order.detail.eventName}
                        </Typography>
                      </Box>
                    </Box>
                    <InfoRow
                      label="时间"
                      value={formatEventTimeRange(
                        order.detail.startTime,
                        order.detail.duration,
                      )}
                    />
                    <InfoRow
                      label="报名费"
                      value={`¥${Number(order.detail.price).toFixed(2)}`}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Course details */}
            {order.detail?.__typename === 'CourseDetail' && (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    课程详情
                  </Typography>
                  <Box>
                    <InfoRow label="课程" value={order.detail.courseName} />
                    <InfoRow label="课时" value={`${order.detail.hours}课时`} />
                    <InfoRow
                      label="课程费"
                      value={`¥${Number(order.detail.price).toFixed(2)}`}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Order info */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  订单信息
                </Typography>
                <InfoRow label="订单号" value={order.id} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    客户
                  </Typography>
                  <UserAvatar user={order.user} />
                </Box>
                <InfoRow
                  label="下单时间"
                  value={dateFormat(order.createTime, {
                    format: 'yyyy-MM-dd HH:mm:ss',
                  })}
                />
                {order.payTime && (
                  <InfoRow
                    label="支付时间"
                    value={dateFormat(order.payTime, { format: 'yyyy-MM-dd HH:mm:ss' })}
                  />
                )}
                {order.cancelTime && (
                  <InfoRow
                    label="取消时间"
                    value={dateFormat(order.cancelTime, {
                      format: 'yyyy-MM-dd HH:mm:ss',
                    })}
                  />
                )}
              </CardContent>
            </Card>

            {/* Payment records */}
            {order.payments.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    支付记录
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>时间</TableCell>
                        <TableCell>操作人</TableCell>
                        <TableCell align="right">金额</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.payments.map((payment) => {
                        const amount = Number(payment.amount)
                        const isRefund = amount < 0
                        const couponId = payment.couponId
                        return (
                          <TableRow key={payment.id}>
                            <TableCell>
                              {payment.payTime
                                ? dateFormat(payment.payTime, {
                                    format: 'yyyy-MM-dd HH:mm:ss',
                                  })
                                : isRefund
                                  ? '退款中'
                                  : dateFormat(payment.createTime, {
                                      format: 'yyyy-MM-dd HH:mm:ss',
                                    })}
                            </TableCell>
                            <TableCell>
                              {couponId != null ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  优惠券抵扣
                                  <Tooltip title="查看优惠券">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setCouponOpen(true)
                                        void loadCoupon({
                                          variables: { couponId },
                                        })
                                      }}
                                      aria-label="查看优惠券"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                (payment.creatorName ?? '-')
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: isRefund ? 'error.main' : 'success.main',
                                fontWeight: 'bold',
                              }}
                            >
                              {isRefund ? '-' : '+'}¥{Math.abs(amount).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  {maxRefund > 0 && isSuperAdmin && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => {
                          setRefundAmount(null)
                          setRefundOpen(true)
                        }}
                      >
                        退款
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            <Dialog
              open={couponOpen}
              onClose={() => setCouponOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>优惠券详情</DialogTitle>
              <DialogContent dividers>
                {couponLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {!couponLoading && coupon && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <InfoRow label="券ID" value={coupon.id} />
                    <InfoRow label="模板名称" value={coupon.template.name} />
                    <InfoRow
                      label="类型"
                      value={
                        COUPON_CATEGORY_LABEL[coupon.template.category] ??
                        coupon.template.category
                      }
                    />
                    <InfoRow
                      label="运动"
                      value={
                        coupon.template.sport
                          ? (SPORT_LABEL[coupon.template.sport] ??
                            coupon.template.sport)
                          : '不限'
                      }
                    />
                    <InfoRow
                      label="状态"
                      value={couponStatusLabels[coupon.status] ?? coupon.status}
                    />
                    <InfoRow
                      label="抵扣金额"
                      value={
                        coupon.discountedAmount != null
                          ? currency(coupon.discountedAmount)
                          : coupon.template.discountAmount != null
                            ? currency(coupon.template.discountAmount)
                            : '—'
                      }
                    />
                    <InfoRow
                      label="发放时间"
                      value={dateFormat(coupon.issuedAt, {
                        format: 'yyyy-MM-dd HH:mm:ss',
                      })}
                    />
                    {coupon.issuer && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          发放人
                        </Typography>
                        <UserAvatar user={coupon.issuer} />
                      </Box>
                    )}
                    <InfoRow label="到期日" value={coupon.expiresAt} />
                    <InfoRow
                      label="使用时间"
                      value={
                        coupon.redeemedAt
                          ? dateFormat(coupon.redeemedAt, {
                              format: 'yyyy-MM-dd HH:mm:ss',
                            })
                          : '—'
                      }
                    />
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCouponOpen(false)}>关闭</Button>
              </DialogActions>
            </Dialog>

            {/* Refund dialog */}
            <Dialog
              open={refundOpen}
              onClose={() => setRefundOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>发起退款</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  label="退款金额"
                  value={refundAmount ?? ''}
                  onChange={(e) =>
                    setRefundAmount(
                      (e as unknown as NumberInputChangeEvent).target.value,
                    )
                  }
                  slotProps={{
                    input: {
                      // NumberInput's onChange takes (event: NumberInputChangeEvent),
                      // which InputBaseComponentProps' standard ChangeEventHandler type
                      // doesn't structurally satisfy.
                      // biome-ignore lint/suspicious/noExplicitAny: see comment above
                      inputComponent: NumberInput as any,
                      inputProps: { min: 0, max: maxRefund, decimalScale: 2 },
                      startAdornment: (
                        <InputAdornment position="start">¥</InputAdornment>
                      ),
                    },
                  }}
                  helperText={refundError || `最多可退 ¥${maxRefund.toFixed(2)}`}
                  error={!!refundError}
                  sx={{ mt: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'warning.main',
                    mt: 1.5,
                    fontWeight: 500,
                  }}
                >
                  注意：退款不会影响订单状态。
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRefundOpen(false)} disabled={refunding}>
                  取消
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={!refundValid || refunding}
                  loading={refunding}
                  onClick={async () => {
                    if (refundAmount === null) return
                    setRefundError('')
                    const result = await refundMutation({
                      variables: {
                        orderId: order?.id ?? '',
                        amount: String(refundAmount),
                      },
                    })
                    const response = result.data?.refundOrder
                    if (response?.__typename === 'GraphqlRequestError') {
                      setRefundError(response.message ?? '退款失败')
                    } else {
                      setRefundOpen(false)
                      setRefundAmount(null)
                      message.success('退款申请已提交', { autoHide: 3000 })
                      setTimeout(refetch, 2000)
                    }
                  }}
                >
                  确认退款
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ userSelect: 'text' }}>
        {value}
      </Typography>
    </Box>
  )
}
